import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  ColumnLayout,
  Container,
  Header,
  SpaceBetween,
  Spinner,
  Icon,
  Alert,
  Button,
} from "@cloudscape-design/components";
import { fetchAuthSession } from "aws-amplify/auth";
import { config } from "../../config";
import "./Statistics.css";

function StatCard({ title, value, icon, description, color, isLoading }) {
  return (
    <div className="stat-card">
      <Container>
        <div className="stat-card-content">
          <div className="stat-card-icon" style={{ backgroundColor: `${color}15` }}>
            <Icon name={icon} size="big" variant="link" />
          </div>
          <div className="stat-card-info">
            <Box variant="awsui-key-label" color="text-body-secondary">
              {title}
            </Box>
            <div className="stat-card-value">
              {isLoading ? (
                <Spinner size="large" />
              ) : (
                <Box variant="h1" fontSize="display-l" fontWeight="bold">
                  {value?.toLocaleString() ?? "—"}
                </Box>
              )}
            </div>
            <Box variant="small" color="text-body-secondary">
              {description}
            </Box>
          </div>
        </div>
      </Container>
    </div>
  );
}

export default function Statistics() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const session = await fetchAuthSession();
      const token = session.tokens.idToken.toString();
      const statsUrl = `${config.controlPlaneAPI}/threat-designer/platform/stats?_t=${Date.now()}`;
      const response = await fetch(statsUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch platform stats");
      }
      const data = await response.json();

      setStats({
        registered_users: data.registered_users,
        threat_models_conducted: data.threat_models_conducted,
        unique_creators: data.unique_creators,
      });
    } catch (err) {
      console.error("Failed to fetch stats:", err);
      setError("Unable to load statistics. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  return (
    <div className="statistics-page">
      <SpaceBetween size="l">
        <div className="statistics-header">
          <Header
            variant="h1"
            description="Platform usage metrics for TUI Threat Designer"
            actions={
              <Button iconName="refresh" onClick={fetchStats} loading={loading}>
                Refresh
              </Button>
            }
          >
            Platform Statistics
          </Header>
        </div>

        {error && (
          <Alert type="error" dismissible onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        <ColumnLayout columns={3} variant="default">
          <StatCard
            title="Registered Users"
            value={stats?.registered_users}
            icon="user-profile"
            description="Total users registered via SSO"
            color="#0972d3"
            isLoading={loading}
          />
          <StatCard
            title="Threat Models Conducted"
            value={stats?.threat_models_conducted}
            icon="security"
            description="Total threat assessments completed"
            color="#037f0c"
            isLoading={loading}
          />
          <StatCard
            title="Unique Creators"
            value={stats?.unique_creators}
            icon="group-active"
            description="Distinct users who created threat models"
            color="#5f6b7a"
            isLoading={loading}
          />
        </ColumnLayout>

        <Container
          header={
            <Header variant="h2" description="Key adoption indicators">
              Insights
            </Header>
          }
        >
          <div className="insights-grid">
            <div className="insight-item">
              <Box variant="awsui-key-label">Adoption Rate</Box>
              <Box variant="h3" fontSize="heading-l">
                {loading ? (
                  <Spinner />
                ) : stats?.registered_users > 0 ? (
                  `${Math.round((stats.unique_creators / stats.registered_users) * 100)}%`
                ) : (
                  "—"
                )}
              </Box>
              <Box variant="small" color="text-body-secondary">
                Percentage of registered users who have created threat models
              </Box>
            </div>
            <div className="insight-item">
              <Box variant="awsui-key-label">Avg Models per Creator</Box>
              <Box variant="h3" fontSize="heading-l">
                {loading ? (
                  <Spinner />
                ) : stats?.unique_creators > 0 ? (
                  (stats.threat_models_conducted / stats.unique_creators).toFixed(1)
                ) : (
                  "—"
                )}
              </Box>
              <Box variant="small" color="text-body-secondary">
                Average number of threat models per active user
              </Box>
            </div>
            <div className="insight-item">
              <Box variant="awsui-key-label">Platform Status</Box>
              <Box variant="h3" fontSize="heading-l" color="text-status-success">
                {loading ? <Spinner /> : "Active"}
              </Box>
              <Box variant="small" color="text-body-secondary">
                Threat Designer is operational
              </Box>
            </div>
          </div>
        </Container>
      </SpaceBetween>
    </div>
  );
}
